import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import type { InitOptions } from 'i18next';
import { DEFAULT_LANGUAGE, isSupportedLanguage, type LanguageCode } from '@/config/languages';
import { DEFAULT_NAMESPACE, NAMESPACES, SHELL_NAMESPACES } from './namespaces';
import { uiLocales } from './negotiate';
import { loadMachineMark } from './machineMark';

/**
 * The i18next runtime (#2109 R1 lane L2; research §2.1 rules 6–7).
 *
 * English rides in the initial chunk: every `src/locales/en/<ns>.json` is
 * bundled eagerly, so a missing translation shows English, never a raw key.
 * The four target locales load lazily through `i18next-resources-to-backend`
 * over a dynamic import — one chunk per `<code>/<ns>.json` — and only when
 * the negotiated locale asks for them (`load: 'currentOnly'`).
 *
 * `lng` is deliberately NOT set here: negotiation (`negotiate.ts`, applied
 * by `LocaleProvider`) decides, so the browser never picks a language on its
 * own and `Accept-Language` is never persisted (research §2.2 rule 7).
 *
 * `initI18n` is idempotent so the vitest setup (`src/test/i18n.ts`, which
 * initialises the same singleton synchronously in `en`, rulings brief B24)
 * and the app entry can both import this module.
 */

type Catalog = Record<string, unknown>;

const EN_MODULES = import.meta.glob<{ default: Catalog }>('../locales/en/*.json', { eager: true });

/** `{ common: {...}, ... }` — every English namespace, keyed by file name. */
export function bundledEnglish(): Record<string, Catalog> {
  const out: Record<string, Catalog> = {};
  for (const [path, mod] of Object.entries(EN_MODULES)) {
    const ns = path.replace(/^.*\//, '').replace(/\.json$/, '');
    out[ns] = mod.default;
  }
  for (const ns of NAMESPACES) {
    if (!(ns in out)) throw new Error(`i18n: NAMESPACES lists "${ns}" but src/locales/en/${ns}.json does not exist`);
  }
  return out;
}

/**
 * The init options, exported for the #2507 test (vitest initialises its own
 * English-only instance first, so `initI18n` is a no-op there).
 *
 * #2507: `ns` is the SHELL namespaces, not all of them. `changeLanguage`
 * loads `ns` before it switches, so it used to wait for every one of 30 lazy
 * chunks, and one that stalled held the whole UI in English for 15 s or more.
 * Now it waits for the chrome only. `preloadLanguage` fetches the rest at the
 * same moment, each lands in the store on its own, and `bindI18nStore:
 * 'added'` re-renders the components reading it. `useSuspense: false` keeps a
 * component whose namespace is still on the wire rendering its English
 * fallback rather than suspending the route.
 */
export function i18nInitOptions(): InitOptions {
  return {
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: uiLocales(),
    ns: [...SHELL_NAMESPACES],
    defaultNS: DEFAULT_NAMESPACE,
    load: 'currentOnly',
    partialBundledLanguages: true,
    resources: { [DEFAULT_LANGUAGE]: bundledEnglish() },
    interpolation: { escapeValue: false },
    returnNull: false,
    // The extractor writes "" for an untranslated key; fall through to English, never render blank.
    returnEmptyString: false,
    react: { useSuspense: false, bindI18nStore: 'added' },
  };
}

/**
 * #2507: start downloading every namespace of `code` now, without switching.
 * The backend connector de-duplicates, so a `changeLanguage` that follows
 * reuses these requests. English is bundled and never fetched.
 */
export function preloadLanguage(code: string | null | undefined, instance: typeof i18n = i18n): void {
  if (!code || code === DEFAULT_LANGUAGE) return;
  const connector = (instance.services as { backendConnector?: { load?: (l: string[], n: string[], cb: () => void) => void } } | undefined)
    ?.backendConnector;
  connector?.load?.([code], [...NAMESPACES], () => {});
}

/**
 * The language a signed-out tab will most likely resolve to, read before React
 * mounts: `?lang=`, then the tab's link language (`sessionStorage`), then the
 * visitor's choice (`localStorage`). Both stores use `trf.lang`, the keys
 * `LocaleProvider` writes (not imported: it imports this module). A guess
 * only. `LocaleProvider` still negotiates, and a wrong guess costs only the
 * download.
 */
export function arrivalLanguage(search: string = typeof window === 'undefined' ? '' : window.location.search): LanguageCode | null {
  const read = (get: () => string | null): string | null => {
    try {
      return get();
    } catch {
      return null;
    }
  };
  for (const candidate of [
    new URLSearchParams(search).get('lang'),
    read(() => window.sessionStorage.getItem('trf.lang')),
    read(() => window.localStorage.getItem('trf.lang')),
  ]) {
    if (isSupportedLanguage(candidate)) return candidate;
  }
  return null;
}

export function initI18n(): typeof i18n {
  if (i18n.isInitialized) return i18n;
  void i18n
    .use(initReactI18next)
    .use(resourcesToBackend((code: string, ns: string) => import(`../locales/${code}/${ns}.json`)))
    .init(i18nInitOptions());
  // #2507: the arrival language's chunks start before the first render.
  preloadLanguage(arrivalLanguage());
  // R3-B16: the per-string "MT" mark, in non-production builds only. The
  // artifact it reads is imported inside the guarded branch of
  // `loadMachineMark`, so a production bundle never carries it.
  if (!import.meta.env.PROD) void loadMachineMark(i18n);
  return i18n;
}

initI18n();

export { i18n };
export default i18n;
